import React, { useEffect } from "react";
import { X } from "lucide-react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Controls drawn beside the title, e.g. the partner's colour picker. */
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "xxl" | "xxxl";
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  headerExtra,
  children,
  size = "lg",
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    xxl: "max-w-6xl",
    xxxl: "max-w-[1600px]",
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`bg-[#FAFAF8] rounded-xl shadow-2xl w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden flex flex-col`}
      >
        <div className="flex justify-between items-center px-4 py-3 sm:p-6 border-b border-[rgba(11,15,14,0.08)] bg-white shrink-0">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-medium text-[#0B0F0E]">{title}</h2>
            {headerExtra}
          </div>
          <button
            // Explicit: a <button> defaults to type="submit", so a modal opened
            // from inside a form would submit it on close.
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-[#F5F4F0] rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-[#8A9590]" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};
